import { type User } from '../../../models/User'
import { isBlankValue, isRecord, schemaProperties, type SchemaNode, type Values } from './schemaValues'
import { type ClientStatus, type ContactOption, type WizardHolder } from './types'

type ContactFact = 'firstName' | 'lastName' | 'fullName' | 'email'

const _FACTS: ContactFact[] = ['firstName', 'lastName', 'fullName', 'email']

export type ContactFacts = Partial<Record<ContactFact, string>>

const _FACT_ANNOTATION = 'x-zocks-fact'

const isFreeText = (node: SchemaNode): boolean => node.type === 'string' && node.enum === undefined && node.oneOf === undefined

const declaredFact = (node: SchemaNode): ContactFact | undefined => {
  if (!isFreeText(node)) {
    return undefined
  }
  const annotated = node[_FACT_ANNOTATION]
  return typeof annotated === 'string' && (_FACTS as string[]).includes(annotated) ? (annotated as ContactFact) : undefined
}

const custodianIdsOf = (integrations: User['integrations']): Record<string, string> | undefined => {
  const linked = (integrations ?? []).filter((integration) => !!integration.serviceId && !!integration.externalId)
  return linked.length > 0 ? Object.fromEntries(linked.map((integration) => [integration.serviceId, integration.externalId])) : undefined
}

export const contactOptionOf = (user: Pick<User, 'id' | 'name' | 'firstName' | 'lastName' | 'email' | 'integrations'>): ContactOption => {
  const words = (user.name ?? '').trim().split(/\s+/).filter(Boolean)
  const split = words.length === 2 ? words : []
  const custodianIds = custodianIdsOf(user.integrations)
  return {
    id: user.id,
    name: user.name,
    firstName: user.firstName?.trim() || split[0],
    lastName: user.lastName?.trim() || split[1],
    email: user.email?.trim() || undefined,
    ...(custodianIds ? { custodianIds } : {}),
  }
}

export const custodianIdOf = (option: ContactOption | undefined, connector: string): string | undefined =>
  connector ? option?.custodianIds?.[connector] : undefined

export const clientStatusOf = (option: ContactOption | undefined, connector: string): ClientStatus => (custodianIdOf(option, connector) ? 'existing' : 'new')

export const filterByClientStatus = (options: ContactOption[], status: ClientStatus, connector: string): ContactOption[] =>
  options.filter((option) => clientStatusOf(option, connector) === status)

export const holderClientStatus = (holder: Pick<WizardHolder, 'clientStatus'>): ClientStatus => holder.clientStatus ?? 'new'

export const contactFacts = (option: ContactOption | undefined): ContactFacts => {
  if (!option) {
    return {}
  }
  const facts: ContactFacts = {}
  if (option.firstName) {
    facts.firstName = option.firstName
  }
  if (option.lastName) {
    facts.lastName = option.lastName
  }
  if (option.name.trim()) {
    facts.fullName = option.name.trim()
  }
  if (option.email) {
    facts.email = option.email
  }
  return facts
}

interface Placement {
  path: string[]
  value: string
}

const placements = (schema: SchemaNode, values: Values, facts: ContactFacts, spent: Set<ContactFact>, path: string[]): Placement[] =>
  Object.entries(schemaProperties(schema)).flatMap(([name, node]) => {
    if (!isRecord(node)) {
      return []
    }
    const value = values[name]
    if (node.type === 'array') {
      const items = isRecord(node.items) ? node.items : undefined
      if (!items || !Array.isArray(value)) {
        return []
      }
      return value.flatMap((entry, index) => placements(items, isRecord(entry) ? entry : {}, facts, spent, [...path, name, String(index)]))
    }
    if (isRecord(node.properties)) {
      return placements(node, isRecord(value) ? value : {}, facts, spent, [...path, name])
    }
    const fact = declaredFact(node)
    if (!fact || spent.has(fact)) {
      return []
    }
    spent.add(fact)
    const answer = facts[fact]
    return answer && isBlankValue(value) ? [{ path: [...path, name], value: answer }] : []
  })

const withValueAt = (container: unknown, path: string[], value: string): unknown => {
  const [head, ...rest] = path
  if (Array.isArray(container)) {
    const next = [...container]
    const index = Number(head)
    next[index] = rest.length === 0 ? value : withValueAt(next[index], rest, value)
    return next
  }
  const record = isRecord(container) ? container : {}
  return { ...record, [head]: rest.length === 0 ? value : withValueAt(record[head], rest, value) }
}

export const fillFromContact = (values: Values, schema: SchemaNode, facts: ContactFacts): Values => {
  if (Object.keys(facts).length === 0) {
    return values
  }
  return placements(schema, values, facts, new Set<ContactFact>(), []).reduce(
    (filled, placement) => withValueAt(filled, placement.path, placement.value) as Values,
    values,
  )
}
